// Single source of truth for the Septima Wealth Vulnerability Assessment (SEP-171) —
// the categorised, weighted question set, the per-option scores, the RAG band
// thresholds and the required review frequency. Lives in `@septima/contract`
// (the wire layer) because the web form, the server controller and any PDF all
// need the identical question set + scoring.
//
// SEP-171 replaced the earlier 15-question flat Yes/No FCA-driver instrument
// (score = count of "Yes", bands low/moderate/high) with the firm's weighted
// CRM instrument: every option carries its own score, the total is the sum of
// the selected options' scores, and the total bands into GREEN / YELLOW / RED.
//
// Nothing derived is ever stored. The single exception is `clientAgeYears`,
// which is CAPTURED on the assessment row at save time (from the Contact's date
// of birth) so a historical assessment does not silently re-score as the client
// ages — the age SCORE is still derived here from that captured integer.
//
// Compliance is expected to send revised weights. Every weight is one `score`
// number in `VULNERABILITY_QUESTIONS` below, so a re-scoring is a
// one-number-per-option edit in this file and nowhere else.

/** The categorised cards the CRM form groups the questions into. */
export type VulnerabilityCategory =
    | 'age_factor'
    | 'health_assessment'
    | 'economic_abuse'
    | 'life_events'
    | 'resilience'
    | 'capability';

/** Render order of the category cards (left-to-right, top-to-bottom). */
export const VULNERABILITY_CATEGORY_ORDER: readonly VulnerabilityCategory[] = [
    'age_factor',
    'health_assessment',
    'economic_abuse',
    'life_events',
    'resilience',
    'capability',
] as const;

/**
 * Human label per category card. A switch rather than a keyed record: the
 * category values are snake_case DOMAIN VALUES, and an object literal would put
 * them in key position, which the snake-case guard (rightly) forbids.
 */
export function vulnerabilityCategoryLabel(category: VulnerabilityCategory): string {
    switch (category) {
        case 'age_factor':
            return 'Age Factor';
        case 'health_assessment':
            return 'Health Assessment';
        case 'economic_abuse':
            return 'Economic Abuse Factors';
        case 'life_events':
            return 'Life Events';
        case 'resilience':
            return 'Resilience';
        case 'capability':
            return 'Capability';
    }
}

/**
 * How an answer is collected.
 *   - `dropdown`  — a closed option list.
 *   - `yes_no`    — the same, restricted to Yes / No.
 *   - `automated` — derived, not answered: the form renders it read-only and
 *     the value never appears in `answers` (see `clientAgeYears`).
 */
export type VulnerabilityQuestionKind = 'dropdown' | 'yes_no' | 'automated';

/** One selectable answer and the score it contributes to the total. */
export interface VulnerabilityOption {
    /** Stored answer value — a closed snake_case domain string. */
    value: string;
    label: string;
    score: number;
}

/** One question on the assessment form. `key` is the stable camelCase answer key. */
export interface VulnerabilityQuestion {
    key: string;
    category: VulnerabilityCategory;
    prompt: string;
    kind: VulnerabilityQuestionKind;
    options: readonly VulnerabilityOption[];
}

/** The automated Age Factor question — scored from the captured `clientAgeYears`. */
export const CLIENT_AGE_QUESTION_KEY = 'clientAge';

/** Yes/No option pair with the risk-bearing answer weighted. */
function yesNo(yesScore: number, noScore = 0): readonly VulnerabilityOption[] {
    return [
        { value: 'no', label: 'No', score: noScore },
        { value: 'yes', label: 'Yes', score: yesScore },
    ] as const;
}

/**
 * The assessment questions in render order, grouped by category. Every option's
 * `score` is the firm's weight for that answer; the total score is the sum of
 * the selected options' scores plus the automated Age Factor score.
 */
export const VULNERABILITY_QUESTIONS: readonly VulnerabilityQuestion[] = [
    // --- Age Factor (automated from the Contact's date of birth) ---
    {
        key: CLIENT_AGE_QUESTION_KEY,
        category: 'age_factor',
        prompt: 'Client Age',
        kind: 'automated',
        options: [
            { value: 'age_18_25', label: '18-25', score: 15 },
            { value: 'age_26_54', label: '26-54', score: 0 },
            { value: 'age_55_64', label: '55-64', score: 5 },
            { value: 'age_65_74', label: '65-74', score: 10 },
            { value: 'age_75_plus', label: '75+', score: 15 },
        ],
    },
    // --- Health Assessment ---
    {
        key: 'physicalMentalHealthImpact',
        category: 'health_assessment',
        prompt: 'Physical and Mental Health Impact',
        kind: 'dropdown',
        options: [
            { value: 'no_health_concerns', label: 'No health concerns', score: 0 },
            { value: 'minor_health_issues', label: 'Minor health issues', score: 5 },
            {
                value: 'health_conditions_requiring_assistance',
                label: 'Health conditions requiring assistance',
                score: 15,
            },
            { value: 'severe_health_impact', label: 'Severe health impact', score: 25 },
        ],
    },
    {
        key: 'cognitiveFunction',
        category: 'health_assessment',
        prompt: 'Cognitive Function',
        kind: 'dropdown',
        options: [
            { value: 'no_concerns', label: 'No concerns', score: 0 },
            { value: 'minor_memory_issues', label: 'Minor memory issues', score: 5 },
            {
                value: 'diagnosed_cognitive_condition',
                label: 'Diagnosed cognitive condition',
                score: 15,
            },
            {
                value: 'severe_cognitive_impairment',
                label: 'Severe cognitive impairment',
                score: 25,
            },
        ],
    },
    {
        key: 'activeLpaInPlace',
        category: 'health_assessment',
        prompt: 'Active LPA in Place',
        kind: 'yes_no',
        options: yesNo(26),
    },
    // --- Economic Abuse Factors ---
    {
        key: 'changesInFinancialBehaviour',
        category: 'economic_abuse',
        prompt: 'Changes in financial behaviour',
        kind: 'yes_no',
        options: yesNo(15),
    },
    {
        key: 'unexplainedOrSubstantialWithdrawals',
        category: 'economic_abuse',
        prompt: 'Unexplained, frequent ad-hoc, or substantial withdrawals',
        kind: 'yes_no',
        options: yesNo(15),
    },
    {
        key: 'erraticBehaviourOrReluctance',
        category: 'economic_abuse',
        prompt: 'Erratic behaviour or reluctance to discuss their decisions',
        kind: 'yes_no',
        options: yesNo(15),
    },
    {
        key: 'changesInBehaviourOrMood',
        category: 'economic_abuse',
        prompt: 'Changes in behaviour or mood, such as signs of depression, anxiety or lack of communication',
        kind: 'yes_no',
        options: yesNo(15),
    },
    // --- Life Events ---
    {
        key: 'recentMajorChanges',
        category: 'life_events',
        prompt: 'Recent Major Changes',
        kind: 'dropdown',
        options: [
            { value: 'no_significant_changes', label: 'No significant changes', score: 0 },
            { value: 'minor_life_changes', label: 'Minor life changes', score: 5 },
            { value: 'single_major_life_event', label: 'Single major life event', score: 15 },
            {
                value: 'multiple_major_life_events',
                label: 'Multiple major life events or ongoing crisis',
                score: 25,
            },
        ],
    },
    // --- Resilience ---
    {
        key: 'financialStability',
        category: 'resilience',
        prompt: 'Financial Stability',
        kind: 'dropdown',
        options: [
            {
                value: 'stable_income_and_adequate_savings',
                label: 'Stable income and adequate savings',
                score: 0,
            },
            { value: 'minor_financial_pressure', label: 'Minor financial pressure', score: 5 },
            {
                value: 'significant_financial_strain',
                label: 'Significant financial strain',
                score: 15,
            },
            {
                value: 'severe_financial_instability',
                label: 'Severe financial instability',
                score: 25,
            },
        ],
    },
    // --- Capability ---
    {
        key: 'financialKnowledgeAndExperience',
        category: 'capability',
        prompt: 'Knowledge and experience in financial matters',
        kind: 'dropdown',
        options: [
            { value: 'no_experience', label: 'No experience', score: 26 },
            { value: 'one_to_three_years', label: '1-3 years', score: 10 },
            { value: 'three_to_five_years', label: '3-5 years', score: 5 },
            { value: 'five_to_ten_years', label: '5-10 years', score: 0 },
            { value: 'ten_years_plus', label: '10 years+', score: 0 },
        ],
    },
    {
        key: 'communicationNeeds',
        category: 'capability',
        prompt: 'Does the client have any specific communication needs?',
        kind: 'dropdown',
        options: [
            { value: 'no', label: 'No', score: 0 },
            { value: 'yes_sight_impairment', label: 'Yes - Sight Impairment', score: 26 },
            { value: 'yes_hearing_impairment', label: 'Yes - Hearing Impairment', score: 26 },
            { value: 'yes_writing_impairment', label: 'Yes - Writing Impairment', score: 26 },
            {
                value: 'yes_it_literacy_impairment',
                label: 'Yes - IT Literacy Impairment',
                score: 26,
            },
            { value: 'yes_other', label: 'Yes - Other', score: 26 },
        ],
    },
    {
        key: 'learningDifficulties',
        category: 'capability',
        prompt: 'Does the client have any specific learning difficulties?',
        kind: 'yes_no',
        options: yesNo(15),
    },
    {
        key: 'literacyNumeracyDifficulties',
        category: 'capability',
        prompt: 'Does the client have any literacy/numeracy difficulties?',
        kind: 'yes_no',
        options: yesNo(15),
    },
    {
        key: 'languageDifficulties',
        category: 'capability',
        prompt: 'Does the client have any language difficulties?',
        kind: 'yes_no',
        options: yesNo(15),
    },
] as const;

/** Question lookup by key — O(1) for the per-answer score. */
const QUESTION_BY_KEY: ReadonlyMap<string, VulnerabilityQuestion> = new Map(
    VULNERABILITY_QUESTIONS.map((q) => [q.key, q]),
);

/** The questions a human actually answers (everything except the automated ones). */
export const VULNERABILITY_ANSWERABLE_QUESTIONS: readonly VulnerabilityQuestion[] =
    VULNERABILITY_QUESTIONS.filter((q) => q.kind !== 'automated');

/**
 * The stored answers, keyed by question key — the option `value` selected.
 * Unanswered = absent / null. Answers to the automated questions are never
 * stored (they are derived), and an unrecognised value scores nothing.
 */
export type VulnerabilityAnswers = Record<string, string | null | undefined>;

/** The RAG band the total score falls into. */
export type VulnerabilityBand = 'green' | 'yellow' | 'red';

// SEP-171 D4 — band thresholds resolving the slide's off-by-one gaps
// (the slide read "<25 green, 26-50 yellow, 50+ red", leaving 25 in no band and
// 50 in two). Green is INCLUSIVE of 25; red starts above 50.
const GREEN_MAX = 25;
const YELLOW_MAX = 50;

/** Required review frequency in months, per band (SEP-171 D4). */
const REVIEW_FREQUENCY_MONTHS: Record<VulnerabilityBand, number> = {
    green: 18,
    yellow: 9,
    red: 6,
};

/** The full CRM band label ("GREEN BAND - Not Vulnerable"). */
export function vulnerabilityBandLabel(band: VulnerabilityBand): string {
    switch (band) {
        case 'green':
            return 'GREEN BAND - Not Vulnerable';
        case 'yellow':
            return 'YELLOW BAND - Vulnerable Characteristics';
        case 'red':
            return 'RED BAND - Heightened Vulnerability';
    }
}

/** The short band label the Contact card and the history grid show. */
export function vulnerabilityBandShortLabel(band: VulnerabilityBand): string {
    switch (band) {
        case 'green':
            return 'Green';
        case 'yellow':
            return 'Yellow';
        case 'red':
            return 'Red';
    }
}

/** Band a total score (SEP-171 D4: green <= 25, yellow 26-50, red > 50). */
export function vulnerabilityBand(score: number): VulnerabilityBand {
    if (score > YELLOW_MAX) return 'red';
    if (score > GREEN_MAX) return 'yellow';
    return 'green';
}

/** Required review frequency in months for a band (green 18, yellow 9, red 6). */
export function vulnerabilityReviewFrequencyMonths(band: VulnerabilityBand): number {
    return REVIEW_FREQUENCY_MONTHS[band];
}

/**
 * The Age Factor option value for a whole-year age, or null when the age is
 * unknown. Ages below 18 fall in the youngest declared band: the slide's table
 * starts at 18, and a minor is at least as vulnerable as an 18-25 year old, so
 * silently scoring them 0 would be the wrong failure.
 */
export function clientAgeOptionValue(years: number | null | undefined): string | null {
    if (years === null || years === undefined || !Number.isFinite(years)) return null;
    if (years >= 75) return 'age_75_plus';
    if (years >= 65) return 'age_65_74';
    if (years >= 55) return 'age_55_64';
    if (years >= 26) return 'age_26_54';
    return 'age_18_25';
}

/** The score a question/option pair contributes; 0 for anything unrecognised. */
export function vulnerabilityOptionScore(
    questionKey: string,
    optionValue: string | null | undefined,
): number {
    if (!optionValue) return 0;
    const question = QUESTION_BY_KEY.get(questionKey);
    if (!question) return 0;
    return question.options.find((o) => o.value === optionValue)?.score ?? 0;
}

/**
 * Whole years between a `YYYY-MM-DD` date of birth and an `asOf` date, or null
 * when either is missing/unparseable. Pure — the caller supplies "now"
 * (`clockNow()` on the server), never `new Date()` in here.
 */
export function ageInYears(
    dateOfBirth: string | null | undefined,
    asOf: string | null | undefined,
): number | null {
    if (!dateOfBirth || !asOf) return null;
    const dob = new Date(`${dateOfBirth.slice(0, 10)}T00:00:00Z`);
    const at = new Date(`${asOf.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(dob.getTime()) || Number.isNaN(at.getTime())) return null;
    let years = at.getUTCFullYear() - dob.getUTCFullYear();
    const beforeBirthday =
        at.getUTCMonth() < dob.getUTCMonth() ||
        (at.getUTCMonth() === dob.getUTCMonth() && at.getUTCDate() < dob.getUTCDate());
    if (beforeBirthday) years -= 1;
    return years >= 0 ? years : null;
}

/**
 * Add whole months to a `YYYY-MM-DD` calendar date, clamping to the last day of
 * the target month (31 Jan + 1 month → 28/29 Feb). Returns null for an
 * unparseable date.
 */
export function addMonthsToDate(date: string | null | undefined, months: number): string | null {
    if (!date) return null;
    const base = new Date(`${date.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(base.getTime())) return null;
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth() + months;
    const day = base.getUTCDate();
    const lastDayOfTarget = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const out = new Date(Date.UTC(year, month, Math.min(day, lastDayOfTarget)));
    return out.toISOString().slice(0, 10);
}

/**
 * The next review date for a completed assessment: completion date + the band's
 * review frequency in months. Null when the assessment has no completion date.
 */
export function vulnerabilityNextReviewDate(
    completionDate: string | null | undefined,
    band: VulnerabilityBand,
): string | null {
    return addMonthsToDate(completionDate, vulnerabilityReviewFrequencyMonths(band));
}

/**
 * Which questionnaire a stored answer map was taken against.
 *   - `empty`   — no answers at all.
 *   - `current` — at least one key belongs to the current instrument.
 *   - `legacy`  — answers exist but NONE key into the current instrument. Rows
 *     written against the pre-SEP-171 15-question Yes/No instrument land here;
 *     they score 0 because nothing they hold is scoreable, and a surface should
 *     label them rather than present that 0 as a real Green result.
 */
export type VulnerabilityAnswerInstrument = 'empty' | 'current' | 'legacy';

/**
 * Whether a row's derived figures may be presented as a real score and band.
 *
 * Only `current` qualifies. A `legacy` row keys into no current question and a
 * fully `empty` one has nothing to key at all — both compute a score of 0,
 * which is the GREEN band, so showing their figures turns "we have not screened
 * this person under this instrument" into "screened, not vulnerable". Every
 * surface that renders a score or a band gates on this: the Contact card, the
 * Client Book KYC tab, the suitability-review prefill and the history grid.
 */
export function isVulnerabilityScoreable(
    derived: Pick<VulnerabilityAssessmentDerived, 'answersInstrument'>,
): boolean {
    return derived.answersInstrument === 'current';
}

/** Classify a stored answer map against the current question set. */
export function classifyVulnerabilityAnswers(
    answers: VulnerabilityAnswers,
): VulnerabilityAnswerInstrument {
    const keys = Object.keys(answers ?? {}).filter(
        (k) => answers[k] !== null && answers[k] !== undefined,
    );
    if (keys.length === 0) return 'empty';
    return keys.some((k) => QUESTION_BY_KEY.has(k)) ? 'current' : 'legacy';
}

export interface VulnerabilityAssessmentDerived {
    /** Sum of the selected options' scores, including the Age Factor. */
    score: number;
    /** Count of answerable questions with a recognised answer. */
    answered: number;
    /** Number of answerable questions (excludes the automated Age Factor). */
    answerable: number;
    /** Per-question contribution, keyed by question key (the form's score boxes). */
    questionScores: Record<string, number>;
    /** The Age Factor option the captured age resolved to, or null. */
    clientAgeOption: string | null;
    band: VulnerabilityBand;
    /** "GREEN BAND - Not Vulnerable" etc. */
    bandLabel: string;
    /** "Green" / "Yellow" / "Red" — the card + grid label. */
    bandShortLabel: string;
    /** 18 / 9 / 6 — the Required Review Frequency. */
    reviewFrequencyMonths: number;
    /** Whether the stored answers key into the current question set. */
    answersInstrument: VulnerabilityAnswerInstrument;
}

/**
 * Whether every answerable question carries a recognised answer.
 *
 * This is the COMPLETION GATE, not a progress indicator. A blank assessment
 * scores 0, and 0 is the GREEN band — so completing an unanswered instrument
 * would publish "Not Vulnerable" plus an 18-month next-review date for a client
 * nobody has screened. Both the server (before dispatching the Complete Action)
 * and the form (before enabling its Complete button) refuse on this predicate,
 * so the rule is stated once.
 */
export function isVulnerabilityAssessmentFullyAnswered(
    derived: Pick<VulnerabilityAssessmentDerived, 'answered' | 'answerable'>,
): boolean {
    return derived.answerable > 0 && derived.answered >= derived.answerable;
}

export interface VulnerabilityComputeInput {
    answers: VulnerabilityAnswers;
    /** The client's age in whole years CAPTURED on the row (SEP-171 D5). */
    clientAgeYears?: number | null;
}

/**
 * Compute the total score, the per-question contributions and the band from the
 * stored answers plus the captured client age. Answers keyed to a question that
 * no longer exists (e.g. rows written against the pre-SEP-171 15-question
 * instrument) contribute nothing and are not counted as answered.
 */
export function computeVulnerabilityAssessment(
    input: VulnerabilityComputeInput,
): VulnerabilityAssessmentDerived {
    const answers = input.answers ?? {};
    const questionScores: Record<string, number> = {};
    let score = 0;
    let answered = 0;

    const clientAgeOption = clientAgeOptionValue(input.clientAgeYears);
    const ageScore = vulnerabilityOptionScore(CLIENT_AGE_QUESTION_KEY, clientAgeOption);
    questionScores[CLIENT_AGE_QUESTION_KEY] = ageScore;
    score += ageScore;

    for (const question of VULNERABILITY_ANSWERABLE_QUESTIONS) {
        const selected = answers[question.key];
        const option = selected ? question.options.find((o) => o.value === selected) : undefined;
        const contribution = option?.score ?? 0;
        questionScores[question.key] = contribution;
        if (option) answered += 1;
        score += contribution;
    }

    const band = vulnerabilityBand(score);
    return {
        score,
        answered,
        answerable: VULNERABILITY_ANSWERABLE_QUESTIONS.length,
        questionScores,
        clientAgeOption,
        band,
        bandLabel: vulnerabilityBandLabel(band),
        bandShortLabel: vulnerabilityBandShortLabel(band),
        reviewFrequencyMonths: vulnerabilityReviewFrequencyMonths(band),
        answersInstrument: classifyVulnerabilityAnswers(answers),
    };
}
